import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Coins, HeartHandshake, Rocket, Zap } from "lucide-react";

const AnimatedPercent = ({ delay = 500 }) => {
  const [val, setVal] = useState(0);

  useEffect(() => {
    let raf;
    let startTime;
    const k1 = 810;
    const k2 = 1350;
    const k3 = 2700;

    const step = (now) => {
      if (startTime === undefined) startTime = now;
      const elapsed = now - startTime - delay;
      if (elapsed < 0) {
        raf = requestAnimationFrame(step);
        return;
      }
      let v;
      if (elapsed < k1) v = Math.round((elapsed / k1) * 10);
      else if (elapsed < k2) v = 10;
      else if (elapsed < k3) v = Math.round(10 + ((elapsed - k2) / (k3 - k2)) * 60);
      else {
        setVal(70);
        return;
      }
      setVal(v);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [delay]);

  const settled = val >= 70;

  return (
    <div className="flex items-baseline leading-none">
      <span
        className={`text-[40px] font-extrabold bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mr-0.5 transition-opacity duration-300 ${
          settled ? "opacity-100" : "opacity-0"
        }`}
      >
        ~
      </span>
      <span className="text-[54px] font-extrabold tabular-nums bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
        {val}
      </span>
      <span className="text-[28px] font-extrabold bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent ml-0.5">
        %
      </span>
    </div>
  );
};

const outcomes = [
  {
    icon: Coins,
    color: "#3b82f6",
    bg: "bg-blue-600",
    text: "text-blue-600",
    soft: "bg-blue-50",
    title: "Profit margins sorted",
    text: "The 38 → 95 math actually lands. Revenue cycle is tight, staff cost is lean — you keep more of every dollar you produce.",
  },
  {
    icon: HeartHandshake,
    color: "#6366f1",
    bg: "bg-indigo-600",
    text: "text-indigo-600",
    soft: "bg-indigo-50",
    title: "Peace of mind",
    text: "Stable team, reliable ops, a practice that runs whether anyone calls out. You're not exhausted by the day-to-day — you have room to lead.",
  },
  {
    icon: Rocket,
    color: "#a855f7",
    bg: "bg-purple-600",
    text: "text-purple-600",
    soft: "bg-purple-50",
    title: "Growth feels effortless",
    text: "Adding a chair, opening a second location, ramping marketing — it stops feeling like a sprint and becomes the natural next step.",
  },
];

const AtomicAutomationSlide = ({ heading, subheading }) => {
  const defaultHeading = (
    <>
      You can only capture this entire opportunity once you pass the{" "}
      <span className="text-blue-600">&ldquo;atomic automation threshold&rdquo;</span>.
    </>
  );
  const defaultSubheading = (
    <>
      The point where enough of your office{" "}
      <span className="font-semibold text-slate-900">runs autonomously</span>{" "}
      — not with disjoint AI tools assisting your team in some tasks, but AI{" "}
      <span className="italic font-semibold text-slate-900">autonomously taking action and completing entire workflows on its own.</span>
    </>
  );

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1200px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        {/* Title */}
        <div className="text-center mb-6 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl lg:text-5xl font-extrabold text-slate-900 mb-3 tracking-tight leading-[1.1]"
          >
            {heading || defaultHeading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.45 }}
            className="text-[19px] lg:text-[22px] text-slate-700 font-medium max-w-[1000px] mx-auto leading-[1.5]"
          >
            {subheading || defaultSubheading}
          </motion.p>
        </div>

        {/* Animated automation gauge */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.1)] p-7 mb-5"
        >
          {/* Header row */}
          <div className="flex items-center justify-between gap-6 mb-5">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-600">
              % of your office running autonomously
            </div>
            <AnimatedPercent delay={500} />
          </div>

          {/* Scale markers */}
          <div className="relative h-[14px] text-[10px] font-bold text-slate-400 tabular-nums mb-1.5">
            <div className="absolute left-0">0%</div>
            <div className="absolute left-[10%] -translate-x-1/2">10%</div>
            <div className="absolute left-[70%] -translate-x-1/2 text-blue-600">70%</div>
            <div className="absolute right-0">100%</div>
          </div>

          {/* Track */}
          <div className="relative h-[64px] w-full rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
            {/* Chasm diagonal stripes */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, transparent 0, transparent 8px, rgba(148,163,184,0.12) 8px, rgba(148,163,184,0.12) 16px)",
              }}
            />

            {/* 0–10%: muted "stitched tools" zone with disjoint-tool dots */}
            <div className="absolute top-0 left-0 h-full w-[10%] bg-slate-200/80 flex items-center justify-center">
              <div className="flex items-center gap-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              </div>
            </div>

            {/* 70–100%: vibrant "atomic" zone */}
            <motion.div
              initial={{ opacity: 0.3 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.9, duration: 0.6 }}
              className="absolute top-0 h-full overflow-hidden"
              style={{ left: "70%", width: "30%" }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600" />
              {/* Shimmer */}
              <motion.div
                initial={{ x: "-120%" }}
                animate={{ x: "320%" }}
                transition={{
                  duration: 2.6,
                  repeat: Infinity,
                  ease: "linear",
                  delay: 3.5,
                  repeatDelay: 1.4,
                }}
                className="absolute inset-y-0 w-[30%] bg-gradient-to-r from-transparent via-white/30 to-transparent"
              />
            </motion.div>

            {/* Threshold vertical line at 70% */}
            <motion.div
              initial={{ scaleY: 0, opacity: 0 }}
              animate={{ scaleY: 1, opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.4 }}
              className="absolute top-0 bottom-0 w-[2px] bg-blue-500 origin-center z-10"
              style={{ left: "calc(70% - 1px)" }}
            />

            {/* Trail fill (path traveled) */}
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: ["0%", "10%", "10%", "72%"] }}
              transition={{
                times: [0, 0.3, 0.5, 1],
                duration: 2.7,
                delay: 0.5,
                ease: "easeOut",
              }}
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-400/25 via-indigo-400/30 to-purple-400/35"
            />

            {/* Burst ring at threshold crossing */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 1.5, 3], opacity: [0, 0.85, 0] }}
              transition={{ delay: 2.55, duration: 0.9, ease: "easeOut" }}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full border-[3px] border-blue-500 z-[15]"
              style={{ left: "70%" }}
            />

            {/* Moving indicator */}
            <motion.div
              initial={{ left: "0%" }}
              animate={{ left: ["0%", "10%", "10%", "72%"] }}
              transition={{
                times: [0, 0.3, 0.5, 1],
                duration: 2.7,
                delay: 0.5,
                ease: "easeOut",
              }}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20"
            >
              <div className="relative">
                {/* Pulse ring (after arrival) */}
                <motion.div
                  initial={{ scale: 1, opacity: 0 }}
                  animate={{ scale: [1, 2.2], opacity: [0.7, 0] }}
                  transition={{ duration: 1.7, repeat: Infinity, delay: 3.3 }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-blue-500"
                />
                {/* Core dot */}
                <div className="relative w-7 h-7 rounded-full bg-white border-[3px] border-blue-600 shadow-[0_4px_14px_rgba(59,130,246,0.45)] flex items-center justify-center">
                  <Zap className="w-3 h-3 text-blue-600" strokeWidth={3} />
                </div>
              </div>
            </motion.div>
          </div>

          {/* 10% callout — fragmented stack */}
          <div className="relative mt-1 h-[30px]">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.4 }}
              className="absolute flex items-start"
              style={{ left: "10%", marginLeft: "-5px" }}
            >
              <svg
                width="72"
                height="26"
                viewBox="0 0 72 26"
                fill="none"
                className="flex-shrink-0"
              >
                <path
                  d="M 5 6 L 5 18 L 68 18"
                  stroke="#94a3b8"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                <path
                  d="M 1 6 L 5 2 L 9 6"
                  stroke="#94a3b8"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              <div className="text-[11px] leading-tight text-slate-600 pt-[12px] whitespace-nowrap">
                <span className="font-semibold text-slate-800">Fragmented software stack</span>
                <span className="text-slate-400"> + </span>
                <span className="font-semibold text-slate-800">point-solution AI tools</span>
              </div>
            </motion.div>
          </div>

        </motion.div>

        {/* Intro for outcomes */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3.1, duration: 0.4 }}
          className="text-center mb-4"
        >
          <p className="text-[15px] lg:text-[17px] text-slate-700 font-medium leading-snug max-w-[900px] mx-auto">
            Because at <span className="font-bold text-blue-700">~70% automation</span>, 3 things happen — the prerequisites for{" "}
            <span className="italic font-semibold text-slate-900">effortless growth</span>:
          </p>
        </motion.div>

        {/* 3 outcome cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {outcomes.map((o, i) => {
            const Icon = o.icon;
            return (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 3.35 + i * 0.15 }}
                className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.1)] px-4 py-3.5 flex flex-col border-t-[4px]"
                style={{ borderTopColor: o.color }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: `${o.color}15`,
                      color: o.color,
                    }}
                  >
                    <Icon className="w-[18px] h-[18px]" strokeWidth={2.2} />
                  </div>
                  <h3 className="text-[15px] font-bold text-slate-900 leading-tight">
                    {o.title}
                  </h3>
                </div>
                <p className="text-[12.5px] text-slate-600 leading-[1.5]">
                  {o.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AtomicAutomationSlide;
